import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { getBalance, listWalletTransactions, type WalletTransaction } from '@/lib/wallet';
import { normalizeCurrency } from '@/lib/currency';
import WalletTopUpModal from '@/components/feature/WalletTopUpModal';

// ─────────────────────────────────────────────────────────────
// Portefeuille Zifek du compte client (/mon-compte/wallet).
//
// Même tunnel que le dashboard marchand : recharge Mobile Money (FeexPay),
// solde crédité côté serveur dès la confirmation de l'opérateur, historique
// complet des opérations (recharges, paiements, remboursements).
// ─────────────────────────────────────────────────────────────

const METHOD_META: Record<string, { label: string; icon: string; className: string }> = {
  wallet: { label: 'Portefeuille', icon: 'ri-wallet-3-line', className: 'bg-primary-100 text-primary-700' },
  admin: { label: 'Administrateur', icon: 'ri-shield-star-line', className: 'bg-secondary-100 text-secondary-700' },
  paypal: { label: 'PayPal', icon: 'ri-paypal-line', className: 'bg-accent-100 text-accent-700' },
  stripe: { label: 'Stripe', icon: 'ri-bank-card-line', className: 'bg-accent-100 text-accent-700' },
  feexpay: { label: 'Mobile Money', icon: 'ri-smartphone-line', className: 'bg-accent-100 text-accent-700' },
  vente_boutique: { label: 'Achat boutique', icon: 'ri-store-3-line', className: 'bg-accent-100 text-accent-700' },
  vente_reservation: { label: 'Réservation', icon: 'ri-calendar-check-line', className: 'bg-accent-100 text-accent-700' },
  retrait: { label: 'Retrait', icon: 'ri-bank-card-2-line', className: 'bg-secondary-100 text-secondary-700' },
  parrainage: { label: 'Parrainage', icon: 'ri-share-circle-line', className: 'bg-accent-100 text-accent-700' },
};

function methodMeta(m: string) {
  return METHOD_META[m] || { label: m, icon: 'ri-bank-card-line', className: 'bg-background-100 text-foreground-600' };
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

interface Props {
  onOpenChat?: () => void;
}

export default function ClientWalletTab({ onOpenChat }: Props) {
  const { user } = useAuth();
  const currency = normalizeCurrency(user?.monaie || 'MAD');

  const [balance, setBalance] = useState<number>(Number(user?.solde) || 0);
  const [txs, setTxs] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [topUpOpen, setTopUpOpen] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const [b, t] = await Promise.all([
        getBalance(user.id),
        listWalletTransactions(user.id, 60),
      ]);
      setBalance(b);
      setTxs(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger votre portefeuille.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { void load(); }, [load]);

  const credited = txs
    .filter((t) => t.type === 'recharge')
    .reduce((sum, t) => sum + Number(t.montant || 0), 0);
  const spent = txs
    .filter((t) => t.type === 'debit')
    .reduce((sum, t) => sum + Number(t.montant || 0), 0);

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Mon portefeuille</h2>
        <p className="text-sm text-foreground-500 mt-1">
          Rechargez votre solde et payez vos commandes sans repasser par Mobile Money.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-foreground-950 text-background-50 rounded-xl p-5 md:p-6 relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-background-50/5"></div>
          <div className="relative">
            <div className="flex items-center gap-2 text-background-50/60 text-xs font-medium mb-2">
              <i className="ri-wallet-3-line"></i>
              Solde disponible
            </div>
            <p className="text-3xl md:text-4xl font-bold font-heading tracking-tight">
              {balance.toLocaleString('fr-FR')} <span className="text-base font-medium text-background-50/60">{currency}</span>
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-[11px] text-background-50/60">
              <span className="flex items-center gap-1.5">
                <i className="ri-arrow-down-line text-accent-300"></i>
                {credited.toLocaleString('fr-FR')} {currency} rechargés
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-arrow-up-line text-accent-300"></i>
                {spent.toLocaleString('fr-FR')} {currency} dépensés
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mt-5">
              <button
                type="button"
                onClick={() => setTopUpOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-50 text-foreground-950 text-xs font-bold whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-add-line"></i>
                Recharger mon solde
              </button>
              {onOpenChat && (
                <button
                  type="button"
                  onClick={onOpenChat}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-background-50/25 text-background-50 text-xs font-medium whitespace-nowrap hover:bg-background-50/10 transition-colors cursor-pointer"
                >
                  <i className="ri-shopping-cart-2-line"></i>
                  Payer mon panier
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
          <div className="flex items-center gap-2 text-foreground-500 text-xs font-medium mb-3">
            <i className="ri-information-line text-accent-500"></i>
            Comment ça marche
          </div>
          <ul className="space-y-2.5 text-xs text-foreground-600 leading-relaxed">
            <li className="flex gap-2">
              <i className="ri-smartphone-line text-accent-600 mt-0.5"></i>
              <span>La recharge se paie en Mobile Money, sans quitter votre compte.</span>
            </li>
            <li className="flex gap-2">
              <i className="ri-shield-check-line text-accent-600 mt-0.5"></i>
              <span>Le solde est crédité par le serveur dès la confirmation de l’opérateur.</span>
            </li>
            <li className="flex gap-2">
              <i className="ri-shopping-bag-3-line text-accent-600 mt-0.5"></i>
              <span>Vous pouvez ensuite régler vos commandes avec ce solde.</span>
            </li>
          </ul>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-600 text-xs flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => { void load(); }} className="underline whitespace-nowrap cursor-pointer">Réessayer</button>
        </div>
      )}

      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-background-200/70">
          <i className="ri-history-line text-accent-500"></i>
          <h3 className="text-sm font-semibold text-foreground-900">Historique des opérations</h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
          </div>
        ) : txs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 px-4 text-center">
            <div className="w-14 h-14 rounded-2xl bg-foreground-100 flex items-center justify-center mb-3">
              <i className="ri-wallet-3-line text-xl text-foreground-500"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-800 mb-1">Aucune opération pour l’instant</p>
            <p className="text-xs text-foreground-500 mb-4">Rechargez votre portefeuille pour commencer.</p>
            <button
              type="button"
              onClick={() => setTopUpOpen(true)}
              className="px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              Recharger mon solde
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-background-200/60">
            {txs.map((tx) => {
              const meta = methodMeta(tx.methode);
              const isCredit = tx.type === 'recharge';
              return (
                <li key={tx.id} className="flex items-center gap-3 px-5 py-3">
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${meta.className}`}>
                    <i className={`${meta.icon} text-sm`}></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-900 truncate">
                      {isCredit ? 'Recharge' : tx.type === 'refund' ? 'Remboursement' : 'Paiement'}
                      <span className="ml-2 text-[11px] font-normal text-foreground-400">{meta.label}</span>
                    </p>
                    <p className="text-[11px] text-foreground-500 truncate">{tx.note || formatDate(tx.created_at)}</p>
                  </div>
                  <span className={`text-sm font-bold whitespace-nowrap ${isCredit ? 'text-accent-600' : 'text-primary-600'}`}>
                    {isCredit ? '+' : '-'}{Number(tx.montant).toLocaleString('fr-FR')} {currency}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <WalletTopUpModal
        open={topUpOpen}
        userId={user?.id || 0}
        currency={currency}
        onClose={() => setTopUpOpen(false)}
        onCredited={() => { void load(); }}
      />
    </div>
  );
}